import React, { useEffect, useState, useCallback } from 'react';
import { Repeat, Sparkles, Loader2, Trash2, Play, Users, Plus, Save, Zap } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, EmptyState, Card, Field, inputCls } from './shared';
import { toast } from 'sonner';

const uid = () => Math.random().toString(36).slice(2, 10);

export default function SequencesPanel() {
  const [items, setItems] = useState([]);
  const [lists, setLists] = useState([]);
  const [editing, setEditing] = useState(null);
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    suiteAPI.listSequences().then(r => setItems(r.data));
    suiteAPI.getLists().then(r => setLists(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!brief.trim()) return toast.error('What is the sequence for?');
    setBusy('ai');
    try {
      const { data } = await suiteAPI.aiSequence({ brief, steps: 5 });
      setEditing(data); setBrief(''); load();
      toast.success('Sequence drafted');
    } catch { toast.error('Generation failed'); }
    finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save');
    try { const { data } = await suiteAPI.saveSequence(editing); setEditing(data); load(); toast.success('Saved'); }
    catch { toast.error('Save failed'); }
    finally { setBusy(''); }
  };

  const enroll = async (id, listId) => {
    if (!listId) return toast.error('Pick a list first');
    const { data } = await suiteAPI.enrollSequence(id, { list_ids: [listId] });
    toast.success(`${data.enrolled} contacts enrolled`);
    load();
  };

  const runNow = async () => {
    const { data } = await suiteAPI.processAutomations();
    toast.success(data.email_unconfigured ? 'Due steps found — connect Resend to deliver' : `${data.sequence_emails_sent} sequence emails sent`);
    load();
  };

  if (editing) {
    return (
      <Panel title="Sequence Builder" subtitle="Timed email steps that run automatically after enrollment." testId="sequence-builder"
        action={
          <div className="flex gap-2">
            <Button onClick={() => setEditing(null)} className="bg-zinc-800 text-white">Back</Button>
            <Button onClick={save} disabled={busy === 'save'} className="bg-[#b8ff00] text-black font-bold" data-testid="sequence-save">
              {busy === 'save' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save
            </Button>
          </div>
        }>
        <div className="grid lg:grid-cols-[340px_1fr] gap-5">
          <Card className="p-5 space-y-3 h-fit">
            <Field label="Name"><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="sequence-name" /></Field>
            <Field label="Description"><Textarea rows={2} value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} className={inputCls} /></Field>
            <Field label="Trigger">
              <select value={editing.trigger} onChange={e => setEditing({ ...editing, trigger: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2">
                <option value="manual">Manual enrollment</option>
                <option value="new_lead">New lead captured</option>
                <option value="list_join">Joins a list</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={!!editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="accent-[#b8ff00]" data-testid="sequence-active" />
              Active (steps will send when due)
            </label>
          </Card>

          <div className="space-y-3">
            {(editing.steps || []).map((s, i) => (
              <Card key={s.id || i} className="p-4 space-y-2" data-testid={`sequence-step-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wider text-[#b8ff00]">Step {i + 1}</span>
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => setEditing(e => ({ ...e, steps: e.steps.filter((_, j) => j !== i) }))}><Trash2 size={12} /></Button>
                </div>
                <div className="grid md:grid-cols-[120px_1fr] gap-2">
                  <Field label="Delay (days)"><Input type="number" value={s.delay_days} onChange={e => setEditing(ed => ({ ...ed, steps: ed.steps.map((x, j) => j === i ? { ...x, delay_days: Number(e.target.value) } : x) }))} className={inputCls} /></Field>
                  <Field label="Subject"><Input value={s.subject} onChange={e => setEditing(ed => ({ ...ed, steps: ed.steps.map((x, j) => j === i ? { ...x, subject: e.target.value } : x) }))} className={inputCls} /></Field>
                </div>
                {(s.blocks || []).map((b, bi) => (
                  <Textarea key={b.id || bi} value={b.text} rows={2} className={`${inputCls} text-xs`}
                    onChange={e => setEditing(ed => ({ ...ed, steps: ed.steps.map((x, j) => j === i ? { ...x, blocks: x.blocks.map((y, k) => k === bi ? { ...y, text: e.target.value } : y) } : x) }))} />
                ))}
                <Button size="sm" className="bg-zinc-800 text-white text-xs" onClick={() => setEditing(ed => ({ ...ed, steps: ed.steps.map((x, j) => j === i ? { ...x, blocks: [...(x.blocks || []), { id: uid(), type: 'text', text: '' }] } : x) }))}><Plus size={12} className="mr-1" /> Add paragraph</Button>
              </Card>
            ))}
            <Button className="bg-zinc-800 text-white" onClick={() => setEditing(e => ({ ...e, steps: [...(e.steps || []), { id: uid(), delay_days: 2, subject: 'Follow up', blocks: [{ id: uid(), type: 'text', text: '' }] }] }))} data-testid="sequence-add-step">
              <Plus size={14} className="mr-1.5" /> Add step
            </Button>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="Email Sequences" subtitle="Automated nurture and follow-up flows that keep working while you sleep." testId="suite-sequences"
      action={
        <div className="flex gap-2">
          <Button onClick={runNow} className="bg-zinc-800 text-white" data-testid="sequence-run"><Zap size={14} className="mr-1.5" /> Run due steps</Button>
          <Button onClick={() => setEditing({ name: 'New Sequence', trigger: 'manual', steps: [], active: false })} className="bg-[#b8ff00] text-black font-bold" data-testid="sequence-new"><Plus size={15} className="mr-1.5" /> New sequence</Button>
        </div>
      }>
      <Card className="p-5 mb-5 space-y-3">
        <Field label="AI sequence writer">
          <Textarea value={brief} onChange={e => setBrief(e.target.value)} rows={2} placeholder="5-email welcome flow for new subscribers of a streetwear brand, ending in a first-purchase discount" className={inputCls} data-testid="sequence-brief" />
        </Field>
        <Button onClick={generate} disabled={busy === 'ai'} className="bg-violet-500 text-white" data-testid="sequence-generate">
          {busy === 'ai' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Write sequence
        </Button>
      </Card>

      {items.length === 0 ? <EmptyState icon={Repeat} text="No sequences yet." /> : (
        <div className="space-y-3">
          {items.map(s => (
            <Card key={s.id} className="p-4" data-testid={`sequence-item-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{s.name} {s.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#b8ff00]/15 text-[#b8ff00] ml-1">active</span>}</p>
                  <p className="text-[11px] text-zinc-500">{(s.steps || []).length} steps · {s.enrolled} enrolled · {s.completed} completed · trigger: {s.trigger?.replace(/_/g, ' ')}</p>
                  {s.description && <p className="text-xs text-zinc-400 mt-1">{s.description}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <select onChange={e => enroll(s.id, e.target.value)} defaultValue="" className="bg-zinc-800 border border-zinc-700 rounded text-[11px] text-zinc-300 px-2 py-1" data-testid={`sequence-enroll-${s.id}`}>
                    <option value="">Enroll list…</option>
                    {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.contact_count})</option>)}
                  </select>
                  <Button size="sm" className="bg-zinc-800 text-white text-xs" onClick={() => setEditing(s)} data-testid={`sequence-edit-${s.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deleteSequence(s.id).then(load)}><Trash2 size={13} /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Panel>
  );
}
